import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSupportArticleUrl } from '@frameio/utils';
import { InlineButton, Text, useActiveWindowBreakpoint } from '@frameio/vapor';
import type { OnSearchSuggestionClick } from '../../../../types';
import { CarouselControls, SuggestionCard } from './components';
import TEXT from './generated/TEXT/SuggestionsCarousel';
import { useCarouselSwipe, useSuggestions } from './hooks';
import styles from './SuggestionsCarousel.module.css';

const SCROLL_DURATION = 300;
const SCROLL_PADDING = 16;
const DEFAULT_CARDS_PER_SUBSET = 4;
const DEFAULT_CARD_WIDTH = 240;

// Get the subset index for a given card index
const getSubsetIndex = (cardIndex: number, cardsPerSubset: number) =>
  Math.floor(cardIndex / cardsPerSubset);

// Get the first card index of a subset
const getSubsetStartIndex = (subsetIndex: number, cardsPerSubset: number) =>
  subsetIndex * cardsPerSubset;

type SuggestionsCarouselProps = {
  onClickSuggestion: OnSearchSuggestionClick;
  cardsPerSubset?: number;
  cardWidth?: number;
};

const LEARN_MORE_ARTICLE_PATH =
  '9101079-enhanced-search-with-media-intelligence#h_8ec1dfd605';

TEXT.DEFINE({
  /** Section title for search suggestions */
  title: 'Suggestions',
  /** Accessibility label for the suggestions carousel */
  carouselLabel: 'Search suggestions carousel',
  /** Link text to learn more about enhanced search */
  learnMore: 'Learn more',
});

export const SuggestionsCarousel = ({
  onClickSuggestion,
  cardsPerSubset = DEFAULT_CARDS_PER_SUBSET,
  cardWidth = DEFAULT_CARD_WIDTH,
}: SuggestionsCarouselProps) => {
  const { suggestions } = useSuggestions();
  const activeBreakpoint = useActiveWindowBreakpoint();
  const isMobile = activeBreakpoint === 'base';

  const [currentIndex, setCurrentIndex] = useState(0);
  const [focusedCardIndex, setFocusedCardIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const rafRef = useRef<number | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  const totalSuggestions = suggestions.length;

  // Calculate which cards can be scrolled into view
  const getScrollableCards = useCallback(() => {
    if (!containerRef.current || !trackRef.current || isMobile) {
      return {
        canGoPrev: currentIndex > 0,
        canGoNext: currentIndex < totalSuggestions - 1,
      };
    }

    // Desktop: Check if we can scroll to previous/next subset
    const currentSubset = getSubsetIndex(currentIndex, cardsPerSubset);
    const canGoPrev = currentSubset > 0;
    const canGoNext =
      getSubsetStartIndex(currentSubset + 1, cardsPerSubset) < totalSuggestions;

    return { canGoPrev, canGoNext };
  }, [currentIndex, totalSuggestions, isMobile, cardsPerSubset]);

  const [canGoPrev, setCanGoPrev] = useState(currentIndex > 0);
  const [canGoNext, setCanGoNext] = useState(
    currentIndex < totalSuggestions - 1
  );

  const scrollToIndex = useCallback(
    (index: number) => {
      if (
        !containerRef.current ||
        !trackRef.current ||
        isScrollingRef.current
      ) {
        return;
      }

      const card = trackRef.current.children[index] as HTMLElement;
      if (!card) return;

      // Set guard flag
      isScrollingRef.current = true;

      // Clear any pending scroll handler updates
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }

      card.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });

      // Update state after scroll completes
      const timeoutId = setTimeout(() => {
        setCurrentIndex(index);
        isScrollingRef.current = false;
        // Calculate button states based on the target index, not currentIndex
        const targetSubset = getSubsetIndex(index, cardsPerSubset);
        const prevEnabled = targetSubset > 0;
        const nextEnabled =
          getSubsetStartIndex(targetSubset + 1, cardsPerSubset) <
          totalSuggestions;
        setCanGoPrev(prevEnabled);
        setCanGoNext(nextEnabled);
      }, SCROLL_DURATION);
      scrollTimeoutRef.current = timeoutId;
    },
    [totalSuggestions, cardsPerSubset]
  );

  const handlePrev = useCallback(() => {
    const currentSubset = getSubsetIndex(currentIndex, cardsPerSubset);
    const prevSubset = Math.max(0, currentSubset - 1);
    const prevSubsetStartIndex = getSubsetStartIndex(
      prevSubset,
      cardsPerSubset
    );

    if (prevSubsetStartIndex !== currentIndex) {
      scrollToIndex(prevSubsetStartIndex);
    }
  }, [currentIndex, scrollToIndex, cardsPerSubset]);

  const handleNext = useCallback(() => {
    const currentSubset = getSubsetIndex(currentIndex, cardsPerSubset);
    const nextSubset = currentSubset + 1;
    const nextSubsetStartIndex = getSubsetStartIndex(
      nextSubset,
      cardsPerSubset
    );

    if (nextSubsetStartIndex < totalSuggestions) {
      scrollToIndex(nextSubsetStartIndex);
    }
  }, [currentIndex, scrollToIndex, totalSuggestions, cardsPerSubset]);

  const handleSwipe = useCallback(
    (direction: 'left' | 'right') => {
      if (direction === 'left' && canGoNext) {
        handleNext();
      } else if (direction === 'right' && canGoPrev) {
        handlePrev();
      }
    },
    [canGoNext, canGoPrev, handleNext, handlePrev]
  );

  const { onTouchStart, onTouchMove, onTouchEnd } =
    useCarouselSwipe(handleSwipe);

  // Swipe handlers are only used on non-mobile touch devicesa
  // We use native scroll with CSS scroll-snap for mobile.
  const touchHandlers = useMemo(() => {
    return !isMobile
      ? {
          onTouchStart,
          onTouchMove,
          onTouchEnd,
        }
      : {};
  }, [isMobile, onTouchStart, onTouchMove, onTouchEnd]);

  const focusCard = (index: number) => {
    const cardRef = cardRefs.current[index];
    if (cardRef) {
      cardRef.focus();
      setFocusedCardIndex(index);
      // Scroll card into view
      cardRef.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  };

  const setCardRef = useCallback(
    (index: number) => {
      return (el: HTMLDivElement | null) => {
        if (el) {
          cardRefs.current[index] = el;
        }
      };
    },
    [cardRefs]
  );

  const handleCarouselKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Only handle navigation if a card is focused
      if (focusedCardIndex === null) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          if (focusedCardIndex > 0) {
            focusCard(focusedCardIndex - 1);
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (focusedCardIndex < totalSuggestions - 1) {
            focusCard(focusedCardIndex + 1);
          }
          break;
        default:
          break;
      }
    },
    [focusedCardIndex, totalSuggestions]
  );

  // Update index based on current scroll position
  const updateScrollIndex = useCallback(() => {
    if (isScrollingRef.current || !containerRef.current || !trackRef.current) {
      return;
    }

    // Find which card is currently snapped (closest to scroll position accounting for padding)
    const scrollLeft = containerRef.current.scrollLeft;
    const targetScrollPosition = scrollLeft + SCROLL_PADDING;
    let closestIndex = 0;
    let closestDistance = Infinity;

    Array.from(trackRef.current.children).forEach((card, index) => {
      const cardElement = card as HTMLElement;
      const cardLeft = cardElement.offsetLeft;
      const distance = Math.abs(cardLeft - targetScrollPosition);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    // Update current index only if it changed
    if (closestIndex !== currentIndex) {
      setCurrentIndex(closestIndex);
      // Update button states using subset logic for consistency
      // Note: We calculate based on closestIndex, not currentIndex, since currentIndex hasn't updated yet
      const targetSubset = getSubsetIndex(closestIndex, cardsPerSubset);
      const prevEnabled = targetSubset > 0;
      const nextEnabled =
        getSubsetStartIndex(targetSubset + 1, cardsPerSubset) <
        totalSuggestions;
      setCanGoPrev(prevEnabled);
      setCanGoNext(nextEnabled);
    }
  }, [currentIndex, totalSuggestions, cardsPerSubset]);

  // Handle scroll events to update currentIndex and button states
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      // Prevent scroll event from bubbling to parent container
      e.stopPropagation();

      // Don't update during programmatic scrolling
      if (
        isScrollingRef.current ||
        !containerRef.current ||
        !trackRef.current
      ) {
        return;
      }

      // Schedule RAF if not already scheduled
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          updateScrollIndex();
        });
      }
    },
    [updateScrollIndex]
  );

  // Cleanup scroll timeout and RAF on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Initialize button states on mount and when container size changes
  useEffect(() => {
    if (!containerRef.current || isMobile) return undefined;

    const updateButtonStates = () => {
      const { canGoPrev: prev, canGoNext: next } = getScrollableCards();
      setCanGoPrev(prev);
      setCanGoNext(next);
    };

    // Initial update
    updateButtonStates();

    // Update on resize
    const resizeObserver = new ResizeObserver(updateButtonStates);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMobile, getScrollableCards]);

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <Text color="tertiary" fontWeight="medium" variant="secondary">
          {TEXT.title}
        </Text>
        <div className={styles.controlsContainer}>
          <InlineButton
            elementType="a"
            href={getSupportArticleUrl(LEARN_MORE_ARTICLE_PATH)}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Text color="link" variant="linkSmall">
              {TEXT.learnMore}
            </Text>
          </InlineButton>
          <CarouselControls
            canGoNext={canGoNext}
            canGoPrev={canGoPrev}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        </div>
      </div>
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <section
        ref={containerRef}
        aria-label={TEXT.carouselLabel}
        aria-roledescription="carousel"
        className={styles.carousel}
        onKeyDown={handleCarouselKeyDown}
        onScroll={isMobile ? undefined : handleScroll}
      >
        <div
          {...touchHandlers}
          ref={trackRef}
          className={styles.track}
          style={{ display: 'flex' }}
        >
          {suggestions.map((suggestion, index) => {
            const isSubsetStart = index % cardsPerSubset === 0;
            return (
              <div
                key={index}
                className={`${styles.card} ${
                  isSubsetStart ? styles.subsetStart : ''
                }`}
                style={{ width: `${cardWidth}px` }}
              >
                <SuggestionCard
                  ref={setCardRef(index)}
                  description={suggestion.description.toString()}
                  onClick={onClickSuggestion}
                  onFocus={() => setFocusedCardIndex(index)}
                  title={suggestion.title.toString()}
                  type={suggestion.type}
                />
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};
